import { React, useRef, useState } from 'react';

import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Box, Button, IconButton, TextField } from '@mui/material';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import MonthDatePicker from '../../components/monthDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDatePtBr, formatIsoDate, formatMonthParam, isValidDate, monthLongNamesPtBr } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatFileSize, parseNumericText } from '../../utils/numberUtils';
import '../css/toiLetter.css';

const NUMERIC_FIELDS = [
    { key: 'valor', label: 'Valor (R$)' },
    { key: 'consumo', label: 'Consumo (kWh)' },
    { key: 'ajuste', label: 'Ajuste (kWh)' },
    { key: 'tarifa', label: 'Tarifa (R$/kWh)' }
];

const EMPTY_NUMBERS = Object.fromEntries(NUMERIC_FIELDS.map(({ key }) => [key, '']));

const CartaTOI = () => {
    const [cc, setCC] = useState('');
    const [numeroToi, setNumeroToi] = useState('');
    const [periodo, setPeriodo] = useState(null);
    const [dataInicial, setDataInicial] = useState(null);
    const [dataFinal, setDataFinal] = useState(null);
    const [emissao, setEmissao] = useState(null);
    const [vencimento, setVencimento] = useState(null);
    const [numbers, setNumbers] = useState(EMPTY_NUMBERS);
    const [carta, setCarta] = useState(null);
    const [isSubmit, setIsSubmit] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const fileInputRef = useRef(null);

    const hasPeriodo = isValidDate(periodo);
    const hasDataInicial = isValidDate(dataInicial);
    const hasDataFinal = isValidDate(dataFinal);
    const hasEmissao = isValidDate(emissao);
    const hasVencimento = isValidDate(vencimento);

    function resetForm() {
        setCC('');
        setNumeroToi('');
        setPeriodo(null);
        setDataInicial(null);
        setDataFinal(null);
        setEmissao(null);
        setVencimento(null);
        setNumbers(EMPTY_NUMBERS);
        setCarta(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    async function submitCartaTOI(e) {
        e.preventDefault();

        if (!hasPeriodo) {
            handleAlerta({ status: 'warning', message: 'Informe o período do TOI.' });
            return;
        }

        if (!hasDataInicial || !hasDataFinal) {
            handleAlerta({ status: 'warning', message: 'Informe a data inicial e a data final.' });
            return;
        }

        if (formatIsoDate(dataInicial) > formatIsoDate(dataFinal)) {
            handleAlerta({ status: 'warning', message: 'A data inicial não pode ser posterior à data final.' });
            return;
        }

        if (!hasEmissao) {
            handleAlerta({ status: 'warning', message: 'Informe a data de emissão.' });
            return;
        }

        if (!hasVencimento) {
            handleAlerta({ status: 'warning', message: 'Informe o vencimento.' });
            return;
        }

        const invalidField = NUMERIC_FIELDS.find(({ key }) => Number.isNaN(parseNumericText(numbers[key], NaN)));
        if (invalidField) {
            handleAlerta({ status: 'warning', message: `${invalidField.label} precisa ser um número válido.` });
            return;
        }

        if (!carta) {
            handleAlerta({ status: 'warning', message: 'Anexe a carta TOI em PDF antes de enviar.' });
            return;
        }

        const formData = new FormData();
        formData.append('carta', carta);
        formData.append('cc', cc);
        formData.append('numero_toi', numeroToi);
        formData.append('periodo', formatMonthParam(periodo));
        formData.append('data_inicial', formatIsoDate(dataInicial));
        formData.append('data_final', formatIsoDate(dataFinal));
        formData.append('emissao', formatIsoDate(emissao));
        formData.append('vencimento', formatIsoDate(vencimento));
        NUMERIC_FIELDS.forEach(({ key }) => {
            formData.append(key, String(parseNumericText(numbers[key])));
        });

        setIsSubmit(true);
        try {
            const response = await fetchFlask('registrarToiSemFatura', formData);
            handleAlerta(response);

            if (response.status === 'success' || response.status === 'warning') {
                resetForm();
            }
        } finally {
            setIsSubmit(false);
        }
    }

    function handleAlerta(response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    function handleNumberChange(key, value) {
        setNumbers((prev) => ({ ...prev, [key]: value }));
    }

    const periodoLabel = hasPeriodo
        ? `${monthLongNamesPtBr[periodo.getMonth()]}/${periodo.getFullYear()}`
        : 'Não informado';
    const completedFields = [
        cc,
        numeroToi,
        hasPeriodo,
        hasDataInicial,
        hasDataFinal,
        hasEmissao,
        hasVencimento,
        ...NUMERIC_FIELDS.map(({ key }) => numbers[key]),
        carta
    ].filter(Boolean).length;

    return (
        <Box className='mainBox toi-letter-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />

            <PageHeader>
                Carta TOI
            </PageHeader>

            <form onSubmit={submitCartaTOI} className='toi-letter-layout'>
                <Box className='toi-letter-workspace'>
                    <Box className='toi-letter-intro'>
                        <Box>
                            <h2>Registro de TOI sem fatura</h2>
                        </Box>
                        <p>
                            Registre o TOI compensado via demonstrativo para gerar a cobrança. A carta anexada substitui o documento da fatura na pasta do cliente.
                        </p>
                    </Box>

                    <Box className='toi-letter-panel'>
                        <Box className='toi-letter-section-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Identificação</strong>
                            </Box>
                        </Box>

                        <Box className='toi-letter-fields-grid'>
                            <TextField
                                required
                                label='Conta Contrato'
                                value={cc}
                                onChange={e => setCC(e.target.value)}
                            />
                            <TextField
                                required
                                label='Número do TOI'
                                inputProps={{ inputMode: 'numeric' }}
                                value={numeroToi}
                                onChange={e => setNumeroToi(e.target.value.replace(/\D/g, ''))}
                            />
                            <MonthDatePicker
                                label='Período'
                                minDate={new Date(2020, 0, 1)}
                                value={periodo}
                                onChange={setPeriodo}
                            />
                            <DayDatePicker
                                required
                                fullWidth
                                label='Data Inicial'
                                value={dataInicial}
                                onChange={setDataInicial}
                            />
                            <DayDatePicker
                                required
                                fullWidth
                                label='Data Final'
                                value={dataFinal}
                                onChange={setDataFinal}
                            />
                            <DayDatePicker
                                required
                                fullWidth
                                label='Data de Emissão'
                                value={emissao}
                                onChange={setEmissao}
                            />
                            <DayDatePicker
                                required
                                fullWidth
                                label='Vencimento'
                                value={vencimento}
                                onChange={setVencimento}
                            />
                        </Box>
                    </Box>

                    <Box className='toi-letter-panel'>
                        <Box className='toi-letter-section-heading'>
                            <BoltRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Valores do demonstrativo</strong>
                            </Box>
                        </Box>

                        <Box className='toi-letter-fields-grid'>
                            {NUMERIC_FIELDS.map(({ key, label }) => (
                                <TextField
                                    key={key}
                                    required
                                    label={label}
                                    inputProps={{ inputMode: 'decimal' }}
                                    value={numbers[key]}
                                    onChange={e => handleNumberChange(key, e.target.value)}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box className='toi-letter-panel'>
                        <Box className='toi-letter-section-heading'>
                            <UploadFileRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Carta TOI</strong>
                            </Box>
                        </Box>

                        <Box className='toi-letter-upload-strip'>
                            <Button
                                variant='outlined'
                                component='label'
                                startIcon={<UploadFileRoundedIcon />}
                                className='toi-letter-upload-button'
                            >
                                Escolher Arquivo
                                <input
                                    ref={fileInputRef}
                                    type='file'
                                    hidden
                                    accept='application/pdf'
                                    onChange={(e) => {
                                        setCarta(e.target.files[0] || null);
                                        e.target.value = '';
                                    }}
                                />
                            </Button>

                            <Box>
                                <strong>{carta ? '1 arquivo' : 'Nenhum anexo'}</strong>
                                <span>PDF da carta, salvo na pasta do cliente como documento da fatura.</span>
                            </Box>
                        </Box>

                        {carta && (
                            <Box className='toi-letter-file-list'>
                                <Box className='toi-letter-file-item'>
                                    <Box>
                                        <strong>{carta.name}</strong>
                                        <span>{formatFileSize(carta.size)}</span>
                                    </Box>
                                    <IconButton
                                        size='small'
                                        color='error'
                                        aria-label={`Remover ${carta.name}`}
                                        onClick={() => setCarta(null)}
                                    >
                                        <DeleteRoundedIcon fontSize='small' />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box className='toi-letter-summary'>
                    <Box className='toi-letter-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do TOI</strong>
                        </Box>
                    </Box>

                    <Box className='toi-letter-summary-list'>
                        <Box>
                            <span>Conta contrato</span>
                            <strong className={!cc ? 'is-empty' : undefined}>{cc || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Número do TOI</span>
                            <strong className={!numeroToi ? 'is-empty' : undefined}>{numeroToi || 'Não informado'}</strong>
                        </Box>
                        <Box>
                            <span>Período</span>
                            <strong className={!hasPeriodo ? 'is-empty' : undefined}>{periodoLabel}</strong>
                        </Box>
                        <Box>
                            <span>Intervalo</span>
                            <strong className={!hasDataInicial && !hasDataFinal ? 'is-empty' : undefined}>
                                {hasDataInicial || hasDataFinal
                                    ? `${hasDataInicial ? formatDatePtBr(dataInicial) : '-'} a ${hasDataFinal ? formatDatePtBr(dataFinal) : '-'}`
                                    : 'Não informado'}
                            </strong>
                        </Box>
                        <Box>
                            <span>Emissão</span>
                            <strong className={!hasEmissao ? 'is-empty' : undefined}>
                                {hasEmissao ? formatDatePtBr(emissao) : 'Não informada'}
                            </strong>
                        </Box>
                        <Box>
                            <span>Vencimento</span>
                            <strong className={!hasVencimento ? 'is-empty' : undefined}>
                                {hasVencimento ? formatDatePtBr(vencimento) : 'Não informado'}
                            </strong>
                        </Box>
                        <Box>
                            <span>Consumo / Ajuste</span>
                            <strong className={!numbers.consumo && !numbers.ajuste ? 'is-empty' : undefined}>
                                {numbers.consumo || numbers.ajuste
                                    ? `${numbers.consumo || '-'} / ${numbers.ajuste || '-'} kWh`
                                    : 'Não informados'}
                            </strong>
                        </Box>
                        <Box>
                            <span>Carta</span>
                            <strong className={!carta ? 'is-empty' : undefined}>{carta ? carta.name : 'Não anexada'}</strong>
                        </Box>
                    </Box>

                    <Box className='toi-letter-attention'>
                        <ReportProblemRoundedIcon />
                        <p>
                            Sem o ajuste a cobrança vira fatura mínima. Se o cálculo retornar erro, a linha já foi gravada: recalcule em Calcular Aluguel usando o número do TOI como nota fiscal.
                        </p>
                    </Box>

                    <Box className='toi-letter-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos obrigatórios</span>
                            <strong>{completedFields}/12 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Registrar TOI
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default CartaTOI;
